import { useEffect } from "react";

// SEO / GEO head manager. Imperatively upserts exactly ONE tag per head key
// (title, description, og:*, twitter:*, canonical, robots, ...) so neither the
// static index.html shell nor the prerendered HTML can ever produce duplicate
// or contradictory head entries. All page JSON-LD is emitted as ONE
// application/ld+json script containing a single @graph.
const SITE = "https://nextech.law";

// Global Organization descriptor — rendered on every page.
// NOTE: NexTech.Law is a consultancy, NOT a law firm. Never use LegalService,
// LawFirm, Attorney or similar types here.
const ORG_NODE = {
  "@type": "Organization",
  "@id": `${SITE}/#organization`,
  name: "NexTech.Law",
  alternateName: "NexTech.Law · Michael Jesse",
  url: SITE,
  logo: `${SITE}/og-default.jpg`,
  description:
    "EU tech-regulation and compliance consultancy focused on the EU Cyber Resilience Act, NIS2, DORA, GDPR and AI Act. Fractional CCO retainer for tech scaleups.",
  founder: { "@id": `${SITE}/#michael` },
  address: {
    "@type": "PostalAddress",
    streetAddress: "Kärntner Straße 4",
    postalCode: "9220",
    addressLocality: "Velden am Wörther See",
    addressRegion: "Kärnten",
    addressCountry: "AT",
  },
  email: "michael.jesse@nextech.law",
  telephone: "+43 664 1315687",
  areaServed: [
    { "@type": "Place", name: "European Union" },
    { "@type": "Country", name: "Austria" },
    { "@type": "Country", name: "Germany" },
    { "@type": "Country", name: "Switzerland" },
  ],
  knowsAbout: [
    "Regulation (EU) 2024/2847 (Cyber Resilience Act)",
    "Directive (EU) 2022/2555 (NIS2)",
    "Regulation (EU) 2016/679 (GDPR)",
    "Regulation (EU) 2024/1689 (AI Act)",
    "Regulation (EU) 2022/2554 (DORA)",
    "ENISA Single Reporting Platform",
  ],
  sameAs: ["https://www.linkedin.com/in/nextechlaw/"],
};

// Person schema — Michael as the practitioner behind the site.
const PERSON_NODE = {
  "@type": "Person",
  "@id": `${SITE}/#michael`,
  name: "Michael Jesse",
  givenName: "Michael",
  familyName: "Jesse",
  honorificPrefix: "Mag.",
  honorificSuffix: "LL.M., CEMS MIM",
  jobTitle: "Jurist · Founder · CRA Specialist",
  description:
    "Austrian jurist and founder specialising in the EU Cyber Resilience Act, NIS2 and product cybersecurity. 20+ years of entrepreneurial experience across 35+ countries.",
  url: SITE,
  image: `${SITE}/og-default.jpg`,
  email: "michael.jesse@nextech.law",
  telephone: "+43 664 1315687",
  worksFor: { "@id": `${SITE}/#organization` },
  alumniOf: {
    "@type": "CollegeOrUniversity",
    name: "Wirtschaftsuniversität Wien (WU Vienna)",
    sameAs: "https://www.wikidata.org/wiki/Q689007",
  },
  knowsAbout: [
    "EU Cyber Resilience Act",
    "NIS2 Directive",
    "Product cybersecurity compliance",
    "Vulnerability disclosure programmes",
    "SBOM & PSIRT programme design",
    "EU Digital Regulation",
  ],
  hasOccupation: {
    "@type": "Occupation",
    name: "Jurist · Founder · CRA Specialist",
    occupationLocation: { "@type": "Place", name: "European Union" },
  },
  nationality: { "@type": "Country", name: "Austria" },
  sameAs: ["https://www.linkedin.com/in/nextechlaw/"],
};

const WEBSITE_NODE = {
  "@type": "WebSite",
  "@id": `${SITE}/#website`,
  url: SITE,
  name: "NexTech.Law",
  publisher: { "@id": `${SITE}/#organization` },
  inLanguage: ["de", "en"],
};

// Paid offering — CRA-Ersteinschätzung (€990 one-off), visibly offered via the
// offer popup on the homepage. Emitted on the homepage only.
const HOME_SERVICE_NODE = {
  "@type": "Service",
  "@id": `${SITE}/#cra-initial-assessment`,
  name: "CRA-Ersteinschätzung / CRA Initial Assessment",
  serviceType: "EU Cyber Resilience Act compliance assessment",
  category: "Compliance Advisory",
  description:
    "Product assessment against the EU Cyber Resilience Act (Regulation 2024/2847): written PDF report, 1-hour expert call with Mag. Michael Jesse, LL.M., CRA readiness scorecard and personal roadmap to conformity by December 2027. Delivery in 5 business days.",
  provider: { "@id": `${SITE}/#organization` },
  areaServed: [
    { "@type": "Place", name: "European Union" },
    { "@type": "Country", name: "Austria" },
    { "@type": "Country", name: "Germany" },
    { "@type": "Country", name: "Switzerland" },
  ],
  audience: {
    "@type": "BusinessAudience",
    audienceType: "Manufacturers of products with digital elements",
  },
  offers: {
    "@type": "Offer",
    price: "990",
    priceCurrency: "EUR",
    priceSpecification: {
      "@type": "PriceSpecification",
      price: 990,
      priceCurrency: "EUR",
      valueAddedTaxIncluded: false,
    },
    availability: "https://schema.org/InStock",
    url: `${SITE}/#offer`,
    validFrom: "2026-08-11",
    eligibleRegion: [{ "@type": "Place", name: "European Union" }],
    seller: { "@id": `${SITE}/#organization` },
  },
};

// ---------- imperative head helpers (exactly one tag per key) ----------
const MANAGED = "data-seo-managed";

function upsertOne(selector, createEl, setAttrs) {
  const found = [...document.head.querySelectorAll(selector)];
  let el = found[0];
  if (!el) {
    el = createEl();
    document.head.appendChild(el);
  }
  found.slice(1).forEach((dup) => dup.remove());
  setAttrs(el);
  el.setAttribute(MANAGED, "1");
}

const upsertMetaName = (name, content) =>
  upsertOne(
    `meta[name="${name}"]`,
    () => {
      const el = document.createElement("meta");
      el.setAttribute("name", name);
      return el;
    },
    (el) => el.setAttribute("content", content),
  );

const upsertMetaProperty = (property, content) =>
  upsertOne(
    `meta[property="${property}"]`,
    () => {
      const el = document.createElement("meta");
      el.setAttribute("property", property);
      return el;
    },
    (el) => el.setAttribute("content", content),
  );

const upsertLink = (rel, href, extra = {}) =>
  upsertOne(
    `link[rel="${rel}"]`,
    () => {
      const el = document.createElement("link");
      el.setAttribute("rel", rel);
      return el;
    },
    (el) => {
      el.setAttribute("href", href);
      Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
    },
  );

function replaceHreflangSet(alternates) {
  document.head
    .querySelectorAll(`link[rel="alternate"][hreflang]`)
    .forEach((el) => el.remove());
  alternates.forEach(({ hreflang, href }) => {
    const el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    el.setAttribute("href", href);
    el.setAttribute(MANAGED, "1");
    document.head.appendChild(el);
  });
}

function replaceJsonLd(graphNodes) {
  document.head
    .querySelectorAll('script[type="application/ld+json"]')
    .forEach((el) => el.remove());
  const strip = (node) => {
    const { "@context": _omit, ...rest } = node;
    return rest;
  };
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute(MANAGED, "1");
  script.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@graph": graphNodes.map(strip),
  });
  document.head.appendChild(script);
}

export const SEO = ({
  title,
  description,
  path = "/",
  image = `${SITE}/og-default.jpg`,
  type = "website",
  lang = "de",
  altLang, // legacy: { hreflang, href }
  alternates, // optional explicit [{ hreflang, href }] — wins over altLang
  noindex = false,
  nofollow = false, // only with noindex — e.g. the internal SRP review route
  jsonLd, // page-specific JSON-LD node(s)
  breadcrumbs, // [{ name, path }] — home is prepended implicitly
}) => {
  const url = `${SITE}${path}`;
  const fullTitle = title
    ? title.includes("NexTech.Law")
      ? title
      : `${title} · NexTech.Law`
    : "NexTech.Law | EU Tech Compliance · CRA · AI Act · NIS2";
  const desc =
    description ||
    "CRA, AI Act und NIS2 sicher umsetzen: Juristische Expertise, unternehmerische Erfahrung und Compliance-Tools für Hersteller und KMU. CRA-Check buchen.";

  // ----- graph assembly -----
  const isHome = path === "/" || path === "";
  const pageNodes = (Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []);
  const hasWebPage = pageNodes.some((n) => n["@type"] === "WebPage");
  const webPageNode = {
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: fullTitle,
    description: desc,
    inLanguage: lang,
    isPartOf: { "@id": `${SITE}/#website` },
  };
  const breadcrumbNode =
    breadcrumbs && breadcrumbs.length
      ? {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "NexTech.Law", item: SITE },
            ...breadcrumbs.map((b, i) => ({
              "@type": "ListItem",
              position: i + 2,
              name: b.name,
              item: `${SITE}${b.path}`,
            })),
          ],
        }
      : null;

  const graphNodes = [
    ORG_NODE,
    PERSON_NODE,
    WEBSITE_NODE,
    ...(hasWebPage ? [] : [webPageNode]),
    ...(isHome ? [HOME_SERVICE_NODE] : []),
    ...(breadcrumbNode ? [breadcrumbNode] : []),
    ...pageNodes,
  ];

  // ----- hreflang set -----
  const otherLang = lang === "de" ? "en" : "de";
  const hreflangSet = alternates
    ? alternates
    : altLang
      ? [
          { hreflang: lang, href: url },
          { hreflang: altLang.hreflang, href: altLang.href },
          { hreflang: "x-default", href: url },
        ]
      : [
          { hreflang: lang, href: url },
          { hreflang: otherLang, href: url },
          { hreflang: "x-default", href: url },
        ];

  const graphKey = JSON.stringify(graphNodes);
  const hreflangKey = JSON.stringify(hreflangSet);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = fullTitle;

    upsertMetaName("description", desc);
    upsertMetaProperty("og:title", fullTitle);
    upsertMetaProperty("og:description", desc);
    upsertMetaProperty("og:type", type);
    upsertMetaProperty("og:url", url);
    upsertMetaProperty("og:image", image);
    upsertMetaProperty("og:site_name", "NexTech.Law");
    upsertMetaProperty("og:locale", lang === "de" ? "de_DE" : "en_US");
    upsertMetaProperty("og:locale:alternate", lang === "de" ? "en_US" : "de_DE");
    upsertMetaName("twitter:card", "summary_large_image");
    upsertMetaName("twitter:title", fullTitle);
    upsertMetaName("twitter:description", desc);
    upsertMetaName("twitter:image", image);
    upsertMetaName("geo.region", "AT-2");
    upsertMetaName("geo.placename", "Velden am Wörther See");
    upsertMetaName("geo.position", "46.6106;14.0392");
    upsertMetaName("ICBM", "46.6106, 14.0392");
    upsertMetaName("author", "Mag. Michael Jesse, LL.M., CEMS MIM");
    upsertMetaName("publisher", "NexTech.Law");
    upsertMetaName(
      "robots",
      noindex
        ? nofollow
          ? "noindex,nofollow"
          : "noindex,follow"
        : "index,follow,max-image-preview:large,max-snippet:-1",
    );
    upsertMetaName("DC.title", fullTitle);
    upsertMetaName("DC.description", desc);
    upsertMetaName("DC.publisher", "NexTech.Law");
    upsertMetaName("DC.language", lang);
    upsertMetaName("DC.type", type === "article" ? "Text.Article" : "InteractiveResource");
    upsertMetaName("DC.rights", "© NexTech.Law");

    upsertLink("canonical", url);
    replaceHreflangSet(JSON.parse(hreflangKey));
    replaceJsonLd(JSON.parse(graphKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullTitle, desc, url, image, type, lang, noindex, nofollow, graphKey, hreflangKey]);

  return null;
};
